/**
 * Contexto de ejecución global (Carlos 2026-08-09).
 *
 * "Jamás se ejecuta un análisis sin proyecto, ni environment, ni workspace. Eso
 * es mandatorio. No puede lanzarse absolutamente nada sin eso."
 *
 * Este provider mantiene la cadena completa que la app tiene seleccionada:
 *
 *     Proyecto de Planeación → Workspace → Environment → Sprint
 *
 * Vive por encima de `WorkspaceProvider` y lo condiciona: el workspace elegible
 * ya no es "cualquiera que yo vea", sino uno de los que ejecutan el proyecto
 * activo. Sin esa restricción se podía tener seleccionado el proyecto de un
 * cliente y el workspace de otro, y el lanzamiento fallaba recién en el servidor.
 *
 * La regla del eje la resuelve el SERVIDOR y aquí no se recalcula (`seleccionado`
 * y `bloqueado` vienen dados): con un proyecto se autoselecciona y se bloquea,
 * con varios arranca en el primero activo. Lo único que se guarda en
 * localStorage es la elección explícita del usuario cuando hay varios, y se
 * descarta si deja de ser válida.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getExecutionContext,
  setExecutionContext,
  type ContextPlanProject,
  type ContextSprint,
  type ContextWorkspace,
} from "../api/executionContext";

export const LS_PLAN_KEY = "sar.activePlanProjectId";
export const LS_SPRINT_KEY = "sar.activeSprintId";

interface ExecutionContextValue {
  proyectos: ContextPlanProject[];
  isLoading: boolean;
  /** Proyecto activo, o null mientras carga / si no hay ninguno asignado. */
  planProject: ContextPlanProject | null;
  planProjectId: string | null;
  setPlanProject: (id: string) => void;
  /** true = un solo proyecto: el eje se muestra fijo, sin desplegable. */
  bloqueado: boolean;
  /** Con texto, no hay contexto con el que lanzar nada y hay que decirlo. */
  motivoSinContexto: string | null;
  /** Workspaces del proyecto activo (los que sí ejecutan ese proyecto). */
  workspaces: ContextWorkspace[];
  workspace: ContextWorkspace | null;
  /** El contexto se cambia por demanda, en cualquier corrida (Carlos 2026-08-23). */
  setWorkspace: (id: string) => void;
  sprints: ContextSprint[];
  sprintId: string | null;
  setSprint: (id: string) => void;
  /** Cadena completa y utilizable: sin esto ningún lanzamiento debe ofrecerse. */
  listoParaLanzar: boolean;
}

const Ctx = createContext<ExecutionContextValue | null>(null);

function leerLS(key: string): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(key);
}

function guardarLS(key: string, value: string | null): void {
  if (typeof window === "undefined") return;
  if (value) window.localStorage.setItem(key, value);
  else window.localStorage.removeItem(key);
}

export function ExecutionContextProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["execution-context"],
    queryFn: getExecutionContext,
  });

  const proyectos = useMemo(() => data?.proyectos ?? [], [data]);
  const bloqueado = data?.bloqueado ?? false;

  // Quién es el proyecto, el workspace y el sprint efectivos lo decide SAR-CORE.
  // Esta app solo lo consume (Carlos 2026-08-23): con la regla repartida en tres
  // pantallas, cada una podía interpretarla a su manera.
  const efectivo = data?.efectivo ?? null;

  const planProjectId = efectivo?.plan_project_id ?? null;

  const planProject = useMemo(
    () => proyectos.find((p) => p.id === planProjectId) ?? null,
    [proyectos, planProjectId],
  );

  const workspaces = useMemo(() => planProject?.workspaces ?? [], [planProject]);

  const workspace = useMemo(
    () => workspaces.find((w) => w.id === efectivo?.workspace_id) ?? null,
    [workspaces, efectivo],
  );

  const sprints = useMemo(() => workspace?.sprints ?? [], [workspace]);
  const sprintId = efectivo?.project_id ?? null;

  // Lo que la pantalla PINTA tiene que quedar FIJADO en la sesión.
  //
  // `efectivo` es lo que el servidor resuelve para MOSTRAR; `vigente` es lo que la
  // sesión declaró y lo único que leen los endpoints —`ContextoDep` responde 409 si no
  // hay—. Cuando el navegador no traía nada en localStorage no se fijaba nada, así que
  // la barra lateral enseñaba proyecto y workspace mientras cada listado que depende
  // del contexto respondía 409 y se pintaba VACÍO, sin decir por qué. El usuario veía
  // un contexto que el backend no reconocía.
  //
  // localStorage sigue mandando cuando existe —es la elección explícita de este
  // navegador—, y donde no llega se fija lo EFECTIVO, que es justo lo que se está
  // mostrando. No es adivinar: el servidor ya publicó esa cadena y la pantalla ya la
  // pintó; lo que faltaba era declararla.
  const propuesto = useRef(false);
  useEffect(() => {
    if (!data || propuesto.current || data.vigente) return;
    const plan = leerLS(LS_PLAN_KEY) ?? efectivo?.plan_project_id ?? null;
    const sprint = leerLS(LS_SPRINT_KEY) ?? efectivo?.project_id ?? null;
    if (!plan) return;
    // El WORKSPACE viaja también, y no es opcional: la cadena se declara ENTERA, sin
    // saltear eslabones —fijar un sprint sin su workspace se rechaza—. Mandarlo desde
    // el sprint guardado sería adivinar; se toma el que el servidor ya resolvió.
    const workspace = sprint === efectivo?.project_id
      ? efectivo?.workspace_id ?? null
      : null;
    propuesto.current = true;
    // Si la propuesta guardada ya no vale, el servidor la rechaza y manda su regla:
    // por eso ese 409 se ignora aquí y no se muestra.
    setExecutionContext(workspace && sprint
      ? { plan_project_id: plan, workspace_id: workspace, project_id: sprint }
      : { plan_project_id: plan })
      .then(() => qc.invalidateQueries({ queryKey: ["execution-context"] }))
      .catch(() => undefined);
  }, [data, efectivo, qc]);

  // Se recuerda lo EFECTIVO, no lo elegido: es lo que el servidor aceptó.
  useEffect(() => guardarLS(LS_PLAN_KEY, planProjectId), [planProjectId]);
  useEffect(() => guardarLS(LS_SPRINT_KEY, sprintId), [sprintId]);

  const fijar = useCallback(
    async (cambio: Parameters<typeof setExecutionContext>[0]) => {
      await setExecutionContext(cambio);
      await qc.invalidateQueries({ queryKey: ["execution-context"] });
    },
    [qc],
  );

  const setPlanProject = useCallback(
    (id: string) => {
      // Cambiar de proyecto invalida el resto: el workspace, el sprint y el service
      // pertenecen al anterior, y arrastrarlos mezclaría dos contextos. Se mandan en
      // null EXPLÍCITO — un campo ausente conservaría su valor.
      //
      // CUÁL workspace queda no se decide aquí: se manda el proyecto y el CORE
      // resuelve el primero utilizable.
      void fijar({ plan_project_id: id, workspace_id: null, project_id: null,
                   service_id: null });
    },
    [fijar],
  );

  const setWorkspace = useCallback(
    (id: string) => {
      // El sprint y el service cuelgan del workspace: cambiarlo los invalida. El
      // proyecto lo deduce el CORE, porque un workspace ya dice de cuál se habla.
      void fijar({ workspace_id: id, project_id: null, service_id: null });
    },
    [fijar],
  );

  const setSprint = useCallback(
    (id: string) => {
      // El service cuelga del sprint: cambiar de sprint lo invalida.
      void fijar({ project_id: id, service_id: null });
    },
    [fijar],
  );

  const listoParaLanzar = Boolean(
    planProjectId && workspace?.utilizable && sprintId,
  );

  return (
    <Ctx.Provider
      value={{
        proyectos,
        isLoading,
        planProject,
        planProjectId,
        setPlanProject,
        bloqueado,
        motivoSinContexto: data?.motivo_sin_contexto ?? null,
        workspaces,
        workspace,
        setWorkspace,
        sprints,
        sprintId,
        setSprint,
        listoParaLanzar,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useExecutionContext(): ExecutionContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "useExecutionContext debe usarse dentro de <ExecutionContextProvider>",
    );
  }
  return ctx;
}
